import { useState } from 'react';
import Icon from './icon';
import {
  COLUMN_GROUPS,
  COLUMN_META,
  PIN_LIMIT,
  defaultColumns,
  groupItems,
  moveInGroup,
  pinCount,
} from '../data/manager-columns';

function ViewColumnsPanel({
  title,
  showBack,
  columns,
  dirty,
  pending,
  error,
  mode,
  onChange,
  onClose,
  onBack,
  onApply,
  onSave,
  onSavePreset,
  onSaveAs,
}) {
  const [open, setOpen] = useState({ person: true, metric: true });
  const pins = pinCount(columns);

  function patch(code, next) {
    onChange(columns.map((item) => (item.code === code ? { ...item, ...next } : item)));
  }

  function toggleVisible(item) {
    if (COLUMN_META[item.code].locked) return;
    patch(item.code, { visible: !item.visible });
  }

  function togglePin(item) {
    if (COLUMN_META[item.code].locked) return;
    if (!item.pinned && pins >= PIN_LIMIT) return;
    patch(item.code, { pinned: !item.pinned });
  }

  function onDrop(code, targetCode) {
    onChange(moveInGroup(columns, code, targetCode));
  }

  return (
    <aside className="filter-panel filter-panel--view" aria-label={title}>
      <div className="filter-panel__head">
        <div className="filter-panel__title">
          {showBack ? (
            <button type="button" className="icon-button" aria-label="Назад" onClick={onBack}>
              <Icon name="chevron-left" size={18} />
            </button>
          ) : null}
          <h2>{title}</h2>
        </div>
        <button type="button" className="icon-button" aria-label="Закрыть" onClick={onClose}>
          <Icon name="x" size={18} />
        </button>
      </div>
      <div className="filter-panel__body view-columns">
        <div className="view-columns__bar">
          <div className="view-columns__legend">
            <span className="view-columns__heading">Колонки</span>
            <span className="view-columns__pins">
              {pins} из {PIN_LIMIT}
              <Icon name="pin-fill" size={20} />
            </span>
          </div>
          <button type="button" className="btn btn--text btn--small" onClick={() => onChange(defaultColumns())}>
            Сбросить
          </button>
        </div>
        {COLUMN_GROUPS.map((group) => {
          const items = groupItems(columns, group.id);
          const visibleCount = items.filter((item) => item.visible).length;
          const expanded = open[group.id];
          return (
            <div key={group.id} className="view-columns__group">
              <button
                type="button"
                className="view-columns__group-head"
                onClick={() => setOpen((value) => ({ ...value, [group.id]: !value[group.id] }))}
              >
                {group.title}
                <span className="view-columns__meta">
                  <span className="view-columns__count">{visibleCount}</span>
                  <Icon name={expanded ? 'chevron-up' : 'chevron-down'} size={20} />
                </span>
              </button>
              {expanded ? (
                <div className="view-columns__rows">
                  {items.map((item) => (
                    <ColumnRow
                      key={item.code}
                      item={item}
                      pinLimit={pins >= PIN_LIMIT}
                      onToggleVisible={() => toggleVisible(item)}
                      onTogglePin={() => togglePin(item)}
                      onDrop={onDrop}
                    />
                  ))}
                </div>
              ) : null}
            </div>
          );
        })}
        {error ? <p className="empty">{error}</p> : null}
      </div>
      <div className={mode === 'create' ? 'filter-panel__foot' : 'filter-panel__foot filter-panel__foot--split'}>
        {mode === 'current' ? (
          <>
            <button type="button" className="btn btn--secondary" disabled={pending} onClick={onSavePreset}>
              Сохранить пресет
            </button>
            <button type="button" className="btn" disabled={pending || !dirty} onClick={onApply}>
              Применить
            </button>
          </>
        ) : null}
        {mode === 'create' ? (
          <button type="button" className="btn" disabled={pending} onClick={onSaveAs}>
            Сохранить как
          </button>
        ) : null}
        {mode === 'edit' ? (
          <>
            <button type="button" className="btn btn--secondary" disabled={pending} onClick={onSaveAs}>
              Сохранить как
            </button>
            <button type="button" className="btn" disabled={pending || !dirty} onClick={onSave}>
              Сохранить
            </button>
          </>
        ) : null}
      </div>
    </aside>
  );
}

function ColumnRow({ item, pinLimit, onToggleVisible, onTogglePin, onDrop }) {
  const meta = COLUMN_META[item.code];
  const locked = Boolean(meta.locked);
  const pinBlocked = !item.pinned && pinLimit;
  const dimmed = !item.visible;
  return (
    <div
      className={dimmed ? 'view-columns__row is-off' : 'view-columns__row'}
      draggable
      onDragStart={(event) => {
        event.dataTransfer.setData('text/plain', item.code);
        event.dataTransfer.effectAllowed = 'move';
      }}
      onDragOver={(event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
      }}
      onDrop={(event) => {
        event.preventDefault();
        onDrop(event.dataTransfer.getData('text/plain'), item.code);
      }}
    >
      <span className="view-columns__handle" aria-hidden="true">
        <Icon name="grabber" size={16} />
      </span>
      <span className="view-columns__name">{meta.label}</span>
      <button
        type="button"
        className={item.pinned ? 'view-columns__icon is-on' : 'view-columns__icon'}
        aria-label={item.pinned ? 'Открепить' : 'Закрепить'}
        disabled={locked || pinBlocked}
        title={pinBlocked ? 'Закрепить можно не больше пяти колонок' : undefined}
        onClick={onTogglePin}
      >
        <Icon name={item.pinned ? 'pin-fill' : 'pin'} size={20} />
      </button>
      <button
        type="button"
        className="view-columns__icon"
        aria-label={item.visible ? 'Скрыть' : 'Показать'}
        disabled={locked}
        onClick={onToggleVisible}
      >
        <Icon name={item.visible ? 'eye' : 'eye-off'} size={20} />
      </button>
    </div>
  );
}

export default ViewColumnsPanel;
